// Every word a user sees, in one place. Plain Swedish, sentence case, short sentences.
// No em or en dashes, no hyphen used as a dash, no semicolons, no exclamation marks:
// shared/test/sv.test.ts fails the build if one slips in.

import type { Role } from './permissions.ts';
import type {
  BookingStatus,
  CostCategory,
  Fuel,
  Ownership,
  ServiceKind,
  VehicleStatus,
  VehicleType,
} from './api-types.ts';

export const sv = {
  app: {
    name: 'Aldorock Fordon',
    wordmark: 'Aldorock',
    product: 'Fordon',
    skipLink: 'Gå till innehållet',
    mainNav: 'Huvudmeny',
    menu: 'Meny',
    closeMenu: 'Stäng menyn',
    loading: 'Hämtar',
    supplier: 'Aldorock AB · Org.nr 559607-5985 · support@aldorock.com',
    switchTenant: 'Byt organisation',
    breadcrumbs: 'Du är här',
  },
  nav: {
    start: 'Start',
    book: 'Boka bil',
    bookings: 'Mina bokningar',
    calendar: 'Kalender',
    vehicles: 'Fordon',
    pools: 'Bilpooler',
    reports: 'Rapporter',
    messages: 'Meddelanden',
    board: 'Tavla',
    admin: 'Administration',
    keys: 'Nyckelskåp',
    logout: 'Logga ut',
  },
  roles: {
    systemforvaltare: 'Systemförvaltare',
    fordonsadministrator: 'Fordonsadministratör',
    fordonsombud: 'Fordonsombud',
    bokare: 'Bokare',
    bokare_for_andra: 'Bokare för andra',
    lasare: 'Läsare',
    koncernadmin: 'Koncernadministratör',
  } satisfies Record<Role, string>,
  login: {
    title: 'Logga in',
    intro:
      'Du loggar in med ditt vanliga konto i organisationen. Aldorock Fordon sparar inga lösenord. Första gången du loggar in skapas du som bokare automatiskt.',
    button: 'Logga in med organisationens konto',
    loggedOut: 'Du är utloggad.',
    noAccess:
      'Ditt konto har inte behörighet till Aldorock Fordon. Kontakta systemförvaltaren i din organisation.',
    failed: 'Inloggningen kunde inte slutföras. Försök igen.',
    denied:
      'Organisationens inloggning nekade kontot. Kontot kan vara avstängt. Kontakta IT eller systemförvaltaren.',
    expired: 'Du har varit inaktiv för länge och har loggats ut. Logga in igen.',
  },
  koncern: {
    switchLabel: 'Organisation',
    switchButton: 'Byt',
  },
  start: {
    title: 'Start',
    welcomeFirst: (name: string) => `Välkommen, ${name}.`,
    firstIntro:
      'Det här är första gången du är här. Din bokning tar tre steg: välj tid, välj bil, bekräfta. Bekräftelsen med garage och kod till nyckelskåpet kommer på skärmen och med e-post.',
    bookFirst: 'Gör din första bokning',
    book: 'Boka bil',
    rebook: 'Boka igen',
    rebookLine: (vehicle: string, pool: string) => `Senast körde du ${vehicle} från ${pool}.`,
    rebookButton: (vehicle: string) => `Boka ${vehicle} igen`,
    upcoming: 'Kommande bokningar',
    upcomingEmpty: 'Du har inga kommande bokningar.',
    past: 'Tidigare bokningar',
    pastEmpty: 'Inga tidigare bokningar.',
    change: 'Ändra',
    cancel: 'Avboka',
    show: 'Visa',
    adminTitle: 'Fordonsparken i dag',
    adminVehicles: 'Fordon i registret',
    adminActive: 'Aktiva fordon',
    adminPools: 'Bilpooler',
    adminBookingsToday: 'Bokningar i dag',
    adminInspections: 'Besiktning inom 30 dagar',
    adminLeases: 'Leasing som löper ut inom 90 dagar',
    adminNoUnit: 'Fordon utan förvaltning',
    adminGo: 'Till fordonsregistret',
  },
  columns: {
    registration: 'Reg.nr',
    vehicle: 'Fordon',
    type: 'Typ',
    make: 'Märke',
    model: 'Modell',
    year: 'Årsmodell',
    fuel: 'Drivmedel',
    seats: 'Säten',
    co2: 'CO2 g/km',
    emissionClass: 'Utsläppsklass',
    odometer: 'Mätarställning',
    inspectionDue: 'Besiktning senast',
    unit: 'Avdelning',
    forvaltning: 'Förvaltning',
    pool: 'Bilpool',
    status: 'Status',
    ownership: 'Ägande',
    leaseEnd: 'Leasing till',
    date: 'Datum',
    time: 'Tid',
    from: 'Från',
    to: 'Till',
    driver: 'Förare',
    bookedBy: 'Bokad av',
    purpose: 'Ärende',
    costCentre: 'Kostnadsställe',
    price: 'Pris',
    number: 'Nummer',
    category: 'Kategori',
    amount: 'Belopp',
    supplier: 'Leverantör',
    description: 'Beskrivning',
    source: 'Källa',
    kind: 'Typ',
    workshop: 'Verkstad',
    cost: 'Kostnad',
    nextDue: 'Nästa',
    event: 'Händelse',
    who: 'Vem',
    km: 'km',
    priority: 'Prioritet',
    note: 'Information',
    slot: 'Plats i skåpet',
    key: 'Nyckel',
    action: 'Åtgärd',
    count: 'Antal',
  },
  yes: 'Ja',
  no: 'Nej',
  none: 'Saknas',
  range: (from: string, to: string) => `${from} till ${to}`,
  vehicleTypes: {
    personbil: 'Personbil',
    latt_lastbil: 'Lätt lastbil',
    tung_lastbil: 'Tung lastbil',
    specialfordon: 'Specialfordon',
    slap: 'Släp',
    cykel: 'Cykel',
    lastcykel: 'Lastcykel',
    eltransportfordon: 'Eltransportfordon',
    ovrigt: 'Övrigt',
  } satisfies Record<VehicleType, string>,
  vehicleStatus: {
    bestalld: 'Beställd',
    aktiv: 'I trafik',
    avstalld: 'Avställd',
    avyttrad: 'Avyttrad',
  } satisfies Record<VehicleStatus, string>,
  fuels: {
    bensin: 'Bensin',
    diesel: 'Diesel',
    el: 'El',
    elhybrid: 'Elhybrid',
    laddhybrid: 'Laddhybrid',
    gas: 'Gas',
    etanol: 'Etanol',
    vatgas: 'Vätgas',
    ingen: 'Inget',
    ovrigt: 'Övrigt',
  } satisfies Record<Fuel, string>,
  ownership: {
    agd: 'Ägd',
    leasing: 'Leasing',
    hyrd: 'Hyrd',
  } satisfies Record<Ownership, string>,
  bookingStatus: {
    bokad: 'Bokad',
    pagaende: 'Pågår',
    avslutad: 'Avslutad',
    makulerad: 'Avbokad',
  } satisfies Record<BookingStatus, string>,
  costCategories: {
    bransle: 'Bränsle',
    forsakring: 'Försäkring',
    skatt: 'Skatt',
    skada: 'Skada',
    dack: 'Däck',
    leasing: 'Leasing',
    service: 'Service',
    verkstad: 'Verkstad',
    tvatt: 'Tvätt',
    parkering: 'Parkering',
    ovrigt: 'Övrigt',
  } satisfies Record<CostCategory, string>,
  serviceKinds: {
    service: 'Service',
    besiktning: 'Besiktning',
    reparation: 'Reparation',
    dackbyte: 'Däckbyte',
    skada: 'Skada',
    ovrigt: 'Övrigt',
  } satisfies Record<ServiceKind, string>,
  sources: {
    manual: 'Registrerad för hand',
    abax: 'Abax körjournal',
    boomr: 'Boomr körjournal',
    bokning: 'Bokning',
    import: 'Inläst fil',
    register: 'Vägtrafikregistret',
    ayvens: 'Ayvens',
    verkstad: 'Verkstaden',
    api: 'API',
    simulator: 'Testregister',
    transportstyrelsen: 'Transportstyrelsen',
  } as Record<string, string>,
  vehicles: {
    title: 'Fordon',
    intro:
      'Alla fordon i organisationen. Sök på registreringsnummer, märke, modell eller förvaltning.',
    searchLabel: 'Sök fordon',
    searchHelp: 'Registreringsnummer, märke, modell, förvaltning eller avdelning.',
    search: 'Sök',
    typeFilter: 'Fordonstyp',
    statusFilter: 'Status',
    unitFilter: 'Förvaltning',
    all: 'Alla',
    results: (n: number, total: number) =>
      n === total ? `${total} fordon` : `${n} av ${total} fordon`,
    empty: 'Inget fordon matchar. Ändra sökningen eller filtren.',
    newVehicle: 'Registrera nytt fordon',
    showMore: (n: number) => `Visa ${n} till`,
    listCaption: 'Fordon',
  },
  vehicleCard: {
    crumb: 'Fordon',
    title: (reg: string, makeModel: string) => (makeModel ? `${reg} ${makeModel}` : reg),
    edit: 'Ändra uppgifter',
    cancelEdit: 'Avbryt',
    save: 'Spara',
    saved: 'Uppgifterna är sparade.',
    refresh: 'Hämta från vägtrafikregistret',
    refreshed: (source: string) => `Uppgifterna hämtades från ${source}.`,
    refreshFailed: 'Registret svarade inte. Försök igen om en stund.',
    sections: {
      register: 'Uppgifter från vägtrafikregistret',
      registerAll: 'Alla uppgifter registret lämnade',
      registerSummary: 'Sammanfattning',
      registerOther: 'Övriga uppgifter',
      organisation: 'Organisation och kontakter',
      economy: 'Ekonomi och kontering',
      placement: 'Placering',
      freeText: 'Fritextfält',
      odometer: 'Mätarställning och körsträcka',
      costs: 'Kostnader',
      service: 'Servicehistorik',
      bookings: 'Bokningar',
      history: 'Historik',
      pool: 'Bilpool och nyckel',
    },
    fields: {
      registrationNumber: 'Registreringsnummer',
      vin: 'Chassinummer',
      status: 'Status',
      vehicleType: 'Fordonstyp',
      make: 'Märke',
      model: 'Modell',
      modelYear: 'Årsmodell',
      colour: 'Färg',
      fuel: 'Drivmedel',
      co2GKm: 'CO2 (g/km)',
      emissionClass: 'Utsläppsklass',
      seats: 'Antal säten',
      kerbWeightKg: 'Tjänstevikt (kg)',
      totalWeightKg: 'Totalvikt (kg)',
      firstRegistered: 'Först i trafik',
      inspectionLast: 'Senast besiktigad',
      inspectionDue: 'Besiktning senast',
      taxAnnualSek: 'Fordonsskatt per år (kr)',
      taxDueMonth: 'Skatt betalas i månad',
      ownership: 'Ägandeform',
      lessor: 'Finansbolag',
      leaseStart: 'Leasing från',
      leaseEnd: 'Leasing till',
      leaseMonthlySek: 'Leasingavgift per månad (kr)',
      acquiredOn: 'Anskaffad',
      acquisitionSek: 'Anskaffningsvärde (kr)',
      odometerKm: 'Mätarställning (km)',
      odometerAt: 'Avläst',
      orgUnitId: 'Avdelning',
      managerName: 'Chef',
      coordinatorName: 'Fordonssamordnare',
      representativeName: 'Fordonsombud',
      parking: 'Parkering',
      charger: 'Laddstolpe',
      reference: 'Referens',
      dimAnsvar: 'Ansvar',
      dimTjanst: 'Tjänst',
      dimAktivitet: 'Aktivitet',
      dimObjekt: 'Objekt',
      dimMotpart: 'Motpart',
      codeStrings: 'Kodsträngar',
      registerSource: 'Registerkälla',
      registerFetchedAt: 'Hämtat',
      externalId: 'Id i tidigare system',
    },
    noRegister: 'Inga uppgifter har hämtats från registret ännu.',
    perMil: 'Kostnad per mil senaste tolv månaderna',
    last12: 'Kostnader senaste tolv månaderna',
    perMilNone: 'Inte beräknad. Det saknas körsträcka för perioden.',
    costsEmpty: 'Inga kostnader registrerade.',
    serviceEmpty: 'Ingen servicehistorik registrerad.',
    odometerEmpty: 'Ingen mätarställning registrerad.',
    bookingsEmpty: 'Inga bokningar.',
    historyEmpty: 'Ingen historik.',
    notInPool: 'Fordonet ingår inte i någon bilpool.',
    poolLine: (pool: string, priority: number) => `${pool}, prioritet ${priority}`,
    slotLine: (cabinet: string, slot: number) => `${cabinet}, plats ${slot}`,
    keyIn: 'Nyckeln sitter i skåpet',
    keyOut: 'Nyckeln är uttagen',
    addCost: 'Registrera kostnad',
    addService: 'Registrera service',
    addOdometer: 'Registrera mätarställning',
    mileagePeriod: (km: number, from: string, to: string) => `${km} km ${from} till ${to}`,
    mileage12: 'Körsträcka senaste tolv månaderna',
  },
  newVehicle: {
    title: 'Registrera nytt fordon',
    crumb: 'Nytt fordon',
    intro:
      'Skriv registreringsnumret. Uppgifterna hämtas från vägtrafikregistret och fylls i. Du lägger till det som bara ni vet, som förvaltning och kontering.',
    plate: 'Registreringsnummer',
    plateHelp: 'Till exempel ABC123 eller ABC12A.',
    fetch: 'Hämta uppgifter',
    fetching: 'Hämtar från registret',
    notFound:
      'Registreringsnumret finns inte i registret. Kontrollera numret eller fyll i uppgifterna själv.',
    fetched: (source: string, n: number) => `${n} uppgifter hämtades från ${source}.`,
    ownFields: 'Era uppgifter',
    create: 'Spara fordonet',
    created: 'Fordonet är registrerat.',
    exists: 'Fordonet finns redan i registret.',
    manual: 'Fyll i utan registret',
    step1: 'Registreringsnummer',
    step2: 'Uppgifter från registret',
    step3: 'Era uppgifter',
  },
  pools: {
    title: 'Bilpooler',
    intro: 'Bilpoolerna i organisationen, deras fordon, nycklar och priser.',
    vehicles: (n: number) => (n === 1 ? '1 fordon' : `${n} fordon`),
    default: 'Standardpool',
    keyMode: {
      skap: 'Nyckelskåp',
      ingen: 'Ingen nyckelutlämning',
      manuell: 'Nyckel hos reception',
    },
    garage: 'Garage',
    address: 'Adress',
    price: 'Pris',
    priceLine: (p: { start: number; perHour: number; perDay: number; perKm: number }) => {
      const parts: string[] = [];
      if (p.start) parts.push(`${p.start} kr per bokning`);
      if (p.perHour) parts.push(`${p.perHour} kr per timme`);
      if (p.perDay) parts.push(`${p.perDay} kr per dygn`);
      if (p.perKm) parts.push(`${p.perKm} kr per km`);
      return parts.length ? parts.join(', ') : 'Ingen debitering';
    },
    member: 'Du kan boka här',
    notMember: 'Du har inte tillgång till den här poolen',
    cabinet: 'Nyckelskåp',
    noCabinet: 'Inget nyckelskåp',
    vehiclesCaption: (pool: string) => `Fordon i ${pool}`,
    adminNote: 'Information från administratören',
    members: 'Medarbetare med tillgång',
    membersAll: 'Alla i organisationen (standardpool)',
  },
  booking: {
    title: 'Boka bil',
    crumb: 'Boka bil',
    steps: { time: 'Tid', vehicle: 'Bil', confirm: 'Bekräfta' },
    stepLabel: (n: number, name: string) => `Steg ${n}: ${name}`,
    stepOf: (n: number) => `Steg ${n} av 3`,
    firstHelp:
      'Välj när du behöver bilen. Vi visar sedan vilka bilar som är lediga. Du kan ändra eller avboka fram till starttiden.',
    pool: 'Bilpool',
    poolHelp: 'Du ser poolerna du har tillgång till. Standardpoolen är förvald.',
    date: 'Datum',
    startTime: 'Från klockan',
    endTime: 'Till klockan',
    endDate: 'Slutdatum',
    multiDay: 'Flera dagar',
    duration: (h: number) =>
      h === 1 ? '1 timme' : h < 24 ? `${h} timmar` : `${Math.round(h / 24)} dygn`,
    next: 'Visa lediga bilar',
    chooseVehicle: 'Välj bil',
    vehicleHelp: 'Bilarna visas i den ordning administratören har satt. Upptagna bilar visas sist.',
    available: 'Ledig',
    taken: 'Upptagen',
    takenUntil: (from: string, to: string) => `Bokad ${from} till ${to}`,
    recentVehicle: 'Du körde den senast',
    choose: 'Välj',
    chosen: 'Vald',
    noVehicles: 'Ingen bil är ledig den tiden. Prova en annan tid eller pool.',
    back: 'Tillbaka',
    driver: 'Förare',
    driverSelf: 'Jag själv',
    driverOther: 'Någon annan',
    driverSearch: 'Sök förare',
    driverHelp: 'Du har behörighet att boka åt andra. Föraren får bekräftelsen.',
    purpose: 'Ärende',
    purposeHelp: 'Frivilligt. Visas i kalendern för administratören.',
    costCentre: 'Kostnadsställe',
    costCentreHelp:
      'Förifyllt från din profil. Ändra om resan ska belasta ett annat kostnadsställe.',
    billable: 'Debiteras',
    notBillable: 'Debiteras inte',
    confirm: 'Bekräfta bokningen',
    summary: 'Din bokning',
    price: 'Beräknat pris',
    priceNone: 'Ingen debitering',
    confirmed: 'Bokningen är klar.',
    confirmedIntro: 'Bekräftelsen har skickats till din e-post. Så här hämtar du bilen.',
    keyCode: 'Kod till nyckelskåpet',
    keyWindow: (from: string, to: string) => `Nyckeln kan hämtas ${from} till ${to}.`,
    keyNone: 'Poolen lämnar inte ut nycklar via skåp. Nyckeln finns i bilen eller hos ansvarig.',
    garage: 'Garage',
    done: 'Till mina bokningar',
    another: 'Boka en till',
    errors: {
      past: 'Starttiden har passerat. Välj en senare tid.',
      order: 'Sluttiden måste vara efter starttiden.',
      tooLong: (h: number) => `En bokning kan vara högst ${h} timmar i den här poolen.`,
      tooFar: (d: number) => `Du kan boka högst ${d} dagar framåt i den här poolen.`,
      taken: 'Bilen hann bli bokad av någon annan. Välj en annan bil.',
      noPool: 'Välj en bilpool.',
      noVehicle: 'Välj en bil.',
      notMember: 'Du har inte tillgång till den här poolen.',
      noDriver: 'Välj en förare.',
    },
  },
  bookingPage: {
    crumb: 'Mina bokningar',
    title: (number: string) => `Bokning ${number}`,
    change: 'Ändra bokningen',
    changeTime: 'Ändra tid',
    changeVehicle: 'Byt bil',
    changeDriver: 'Byt förare',
    cancel: 'Avboka',
    cancelTitle: 'Avboka bokningen',
    cancelHelp: 'Bilen blir ledig för andra direkt. Du får en bekräftelse på avbokningen.',
    cancelReason: 'Orsak (frivilligt)',
    cancelConfirm: 'Ja, avboka',
    cancelled: 'Bokningen är avbokad.',
    keep: 'Behåll bokningen',
    saved: 'Bokningen är ändrad. En ändringsbekräftelse har skickats.',
    history: 'Händelser',
    readOnly: 'Bokningen har passerat och kan inte ändras.',
    ownBookings: 'Mina bokningar',
    allBookings: 'Alla bokningar',
    events: {
      bokad: 'Bokad',
      andrad: 'Ändrad',
      makulerad: 'Avbokad',
      nyckel_ut: 'Nyckel uttagen',
      nyckel_in: 'Nyckel återlämnad',
      nyckel_nekad: 'Nyckel nekad utanför bokad tid',
      paminnelse: 'Påminnelse skickad',
      bekraftelse: 'Bekräftelse skickad',
      avslutad: 'Avslutad',
    } as Record<string, string>,
    kmLine: (start: number | null, end: number | null) =>
      start !== null && end !== null
        ? `${end - start} km (${start} till ${end})`
        : start !== null
          ? `Start ${start} km`
          : 'Ingen mätarställning',
  },
  calendar: {
    title: 'Kalender',
    intro: 'Alla bokningar i poolen vecka för vecka. Klicka på en dag för att boka den.',
    pool: 'Bilpool',
    week: (iso: string) => `Vecka ${iso}`,
    previous: 'Föregående vecka',
    next: 'Nästa vecka',
    today: 'I dag',
    caption: (pool: string, from: string, to: string) => `${pool}, ${from} till ${to}`,
    free: 'Ledig',
    book: (vehicle: string, date: string) => `Boka ${vehicle} ${date}`,
    bookShort: 'Boka',
    own: 'Din bokning',
    showBooking: (number: string) => `Visa bokning ${number}`,
    vehicleColumn: 'Fordon',
  },
  messages: {
    title: 'Meddelanden',
    intro: 'Bekräftelser och påminnelser som har skickats till dig. Samma text som i e-posten.',
    empty: 'Inga meddelanden.',
    sent: 'Skickat',
    pending: 'Väntar på utskick',
    failed: 'Kunde inte skickas',
  },
  mail: {
    from: 'Aldorock Fordon <noreply@fordon.aldorock.com>',
    confirmSubject: (number: string, vehicle: string) =>
      `Bokningsbekräftelse ${number}: ${vehicle}`,
    changeSubject: (number: string, vehicle: string) => `Ändringsbekräftelse ${number}: ${vehicle}`,
    cancelSubject: (number: string, vehicle: string) => `Avbokning ${number}: ${vehicle}`,
    reminderSubject: (number: string, vehicle: string) => `Påminnelse ${number}: ${vehicle}`,
    body: (p: {
      kind: 'bekraftelse' | 'andring' | 'avbokning' | 'paminnelse';
      name: string;
      vehicle: string;
      garage: string;
      code: string | null;
      from: string;
      to: string;
      keyFrom: string | null;
      keyTo: string | null;
      link: string;
    }) => {
      const head =
        p.kind === 'bekraftelse'
          ? 'Din bokning är klar.'
          : p.kind === 'andring'
            ? 'Din bokning är ändrad. Så här gäller nu.'
            : p.kind === 'avbokning'
              ? 'Din bokning är avbokad.'
              : 'Påminnelse om din bokning.';
      const lines = [
        `Hej ${p.name},`,
        '',
        head,
        '',
        `Fordon: ${p.vehicle}`,
        `Garage: ${p.garage}`,
        `Datum och tid: ${p.from} till ${p.to}`,
      ];
      if (p.kind !== 'avbokning' && p.code) {
        lines.push(`Kod till nyckelskåpet: ${p.code}`);
        if (p.keyFrom && p.keyTo) lines.push(`Nyckeln kan hämtas ${p.keyFrom} till ${p.keyTo}.`);
      }
      lines.push('', `Bokningen: ${p.link}`, '', 'Aldorock Fordon');
      return lines.join('\n');
    },
  },
  keys: {
    title: 'Nyckelskåp',
    intro:
      'Skåpen, deras platser och vilken bil som hör till vilken nyckel. Skåpet lämnar bara ut nyckeln med en kod som gäller just nu.',
    cabinet: 'Skåp',
    vendor: {
      simulator: 'Simulator',
      proxersafe: 'ProxerSafe KeySafe Lock',
      creone: 'Creone KeyWin',
      traka: 'Traka',
    } as Record<string, string>,
    status: { ok: 'I drift', fel: 'Fel', okand: 'Okänd' } as Record<string, string>,
    lastContact: 'Senaste kontakt',
    slots: (cabinet: string) => `Platser i ${cabinet}`,
    slot: 'Plats',
    empty: 'Tom plats',
    keyIn: 'Nyckel i skåpet',
    keyOut: 'Nyckel uttagen',
    link: 'Koppla bil',
    linkLabel: (slot: number) => `Registreringsnummer för plats ${slot}`,
    linkButton: 'Spara kopplingen',
    unlink: 'Ta bort kopplingen',
    linked: 'Kopplingen är sparad.',
    unknownPlate: 'Registreringsnumret finns inte i fordonsregistret.',
    simulator: 'Prova skåpet',
    simulatorIntro:
      'Skåpet frågar systemet om koden gäller just nu. Samma anrop gör ett riktigt skåp över sitt API. Skriv en kod från en bokning och tryck på knappen.',
    code: 'Kod',
    takeKey: 'Hämta nyckeln',
    returnKey: 'Lämna tillbaka nyckeln',
    accepted: (plate: string, slot: number) =>
      `Luckan för plats ${slot} öppnas. Nyckel till ${plate}.`,
    returned: (plate: string, slot: number) =>
      `Nyckeln till ${plate} är tillbaka på plats ${slot}.`,
    rejectedWindow: (from: string, to: string) =>
      `Koden gäller inte nu. Nyckeln kan hämtas ${from} till ${to}.`,
    rejectedUnknown: 'Koden hör inte till någon bokning i det här skåpet.',
    rejectedOut: 'Nyckeln är redan uttagen.',
    rejectedCancelled: 'Bokningen är avbokad.',
    events: 'Händelser i skåpet',
    eventKinds: {
      uttag: 'Nyckel uttagen',
      aterlamning: 'Nyckel återlämnad',
      nekad: 'Nekad',
    } as Record<string, string>,
    eventsEmpty: 'Inga händelser ännu.',
  },
  poolAdmin: {
    title: 'Administrera bilpooler',
    crumb: 'Bilpooler',
    edit: 'Ändra poolen',
    newPool: 'Ny bilpool',
    name: 'Namn',
    garage: 'Garage',
    address: 'Adress',
    description: 'Beskrivning för bokaren',
    adminNote: 'Information som visas i bokningssidan och kalendern',
    isDefault: 'Standardpool som alla kan boka i',
    keyMode: 'Nyckelutlämning',
    keyWindow: 'Nyckeln kan hämtas så här många minuter före och efter bokningen',
    priceStart: 'Pris per bokning (kr)',
    pricePerHour: 'Pris per timme (kr)',
    pricePerDay: 'Pris per dygn (kr)',
    pricePerKm: 'Pris per km (kr)',
    maxHours: 'Längsta bokning (timmar)',
    maxDaysAhead: 'Bokas högst så här många dagar framåt',
    limitsHelp: 'Lämna tomt för ingen gräns.',
    save: 'Spara poolen',
    saved: 'Poolen är sparad.',
    vehicles: 'Fordon i poolen',
    addVehicle: 'Lägg till fordon',
    addVehicleLabel: 'Registreringsnummer',
    addVehicleButton: 'Lägg till',
    priority: 'Prioritet',
    note: 'Information om fordonet',
    active: 'Bokningsbar',
    remove: 'Ta bort ur poolen',
    removed: 'Fordonet är borttaget ur poolen.',
    swap: 'Byt ut',
    swapTitle: (plate: string) => `Byt ut ${plate}`,
    swapHelp: 'Den nya bilen tar över plats i skåpet, prioritet och kommande bokningar.',
    swapButton: 'Byt ut bilen',
    swapped: 'Bilen är utbytt.',
    members: 'Medarbetare med tillgång',
    membersDefault: 'Standardpoolen är öppen för alla som loggar in.',
    addMember: 'Lägg till medarbetare',
    removeMember: 'Ta bort',
    vehicleSaved: 'Fordonet är sparat.',
    notInRegister: 'Registreringsnumret finns inte i fordonsregistret.',
    alreadyInPool: 'Fordonet ingår redan i en pool.',
    hasBookings: 'Fordonet har kommande bokningar. Byt ut det i stället.',
  },
  bookingAdmin: {
    billable: 'Debiteras',
    notBillable: 'Debiteras inte',
    setBillable: 'Markera som debiterbar',
    setNotBillable: 'Markera som ej debiterbar',
  },
  reports: {
    title: 'Rapporter',
    intro:
      'Bygg en rapport av valda kolumner, filter och period. Tabellen söks och sorteras direkt. Exportera till Excel eller CSV, och spara rapporten som mall.',
    source: 'Rapporttyp',
    sources: {
      fordon: 'Fordon',
      service: 'Servicehistorik',
      bilpool: 'Bilpool per månad',
    } as Record<string, string>,
    period: 'Period',
    from: 'Från',
    to: 'Till',
    columns: 'Kolumner',
    groups: {
      fordon: 'Fordonsdata',
      organisation: 'Organisation',
      ekonomi: 'Ekonomi',
      period: 'För vald period',
      miljo: 'Miljö',
      fritext: 'Fritextfält',
      service: 'Service',
      bilpool: 'Bilpool',
    } as Record<string, string>,
    column: {
      registrationNumber: 'Reg.nr',
      make: 'Märke',
      model: 'Fordonsmodell',
      vehicleType: 'Fordonstyp',
      modelYear: 'Årsmodell',
      fuel: 'Drivmedel',
      seats: 'Antal säten',
      status: 'Status',
      firstRegistered: 'Först i trafik',
      inspectionDue: 'Besiktning senast',
      odometerKm: 'Mätarställning',
      co2GKm: 'CO2 g/km',
      emissionClass: 'Utsläppsklass',
      co2PeriodKg: 'CO2 i perioden (kg)',
      forvaltning: 'Förvaltning',
      unit: 'Avdelning',
      poolName: 'Bilpool',
      managerName: 'Chef',
      coordinatorName: 'Fordonssamordnare',
      representativeName: 'Fordonsombud',
      parking: 'Parkering',
      ownership: 'Ägandeform',
      lessor: 'Finansbolag',
      leaseEnd: 'Leasing till',
      leaseMonthlySek: 'Leasing per månad',
      dimAnsvar: 'Ansvar',
      dimTjanst: 'Tjänst',
      dimAktivitet: 'Aktivitet',
      dimObjekt: 'Objekt',
      dimMotpart: 'Motpart',
      mileagePeriodKm: 'Körsträcka i perioden',
      costPeriodSek: 'Kostnad i perioden',
      costPerMilSek: 'Kostnad per mil',
      fuelCostPeriodSek: 'Bränsle i perioden',
      bookingsPeriod: 'Bokningar i perioden',
      bookedHoursPeriod: 'Bokade timmar',
      utilisationPercent: 'Utnyttjandegrad (%)',
      lastService: 'Senaste service',
      doneOn: 'Datum',
      kind: 'Typ',
      workshop: 'Verkstad',
      description: 'Beskrivning',
      costSek: 'Kostnad',
      nextDueOn: 'Nästa',
      month: 'Månad',
      bookings: 'Bokningar',
      bookedHours: 'Bokade timmar',
      kmDriven: 'Körda km',
      incomeSek: 'Intäkt',
    } as Record<string, string>,
    filters: 'Filter',
    filterType: 'Fordonstyp',
    filterStatus: 'Status',
    filterFuel: 'Drivmedel',
    filterUnit: 'Förvaltning',
    filterPool: 'Bilpool',
    all: 'Alla',
    run: 'Visa rapporten',
    result: 'Rapport',
    rows: (n: number) => (n === 1 ? '1 rad' : `${n} rader`),
    rowsOf: (n: number, total: number) => `${n} av ${total} rader`,
    quickFilter: 'Sök i rapporten',
    quickFilterHelp: 'Söker i alla kolumner medan du skriver.',
    sortBy: (col: string) => `Sortera efter ${col}`,
    sortedAsc: 'stigande',
    sortedDesc: 'fallande',
    exportXlsx: 'Exportera till Excel',
    exportCsv: 'Exportera till CSV',
    save: 'Spara som rapportmall',
    saveName: 'Namn på rapportmallen',
    saveShared: 'Dela med alla som ser rapporter',
    saveButton: 'Spara mallen',
    saved: 'Rapportmallen är sparad.',
    templates: 'Sparade rapporter',
    standard: 'Standardrapporter',
    standardNames: {
      utnyttjandegrad: 'Utnyttjandegrad',
      kostnad_per_km: 'Kostnad per km och fordon',
      servicehistorik: 'Servicehistorik',
      korstrackor: 'Körsträckor',
      bilpoolsbelaggning: 'Bilpoolsbeläggning per månad',
      saten: 'Antal säten',
      miljo: 'Miljörapport med CO2 och utsläppsklass',
    } as Record<string, string>,
    load: (name: string) => `Öppna ${name}`,
    remove: (name: string) => `Ta bort ${name}`,
    byOwner: (name: string) => `av ${name}`,
    shared: 'delad',
    empty: 'Inga rader för valet. Ändra period eller filter.',
    noColumns: 'Välj minst en kolumn.',
    pickColumns: 'Välj kolumner',
    chosen: (n: number) => (n === 1 ? '1 kolumn vald' : `${n} kolumner valda`),
    generated: (at: string) => `Framtagen ${at}`,
    scope: (s: string) => `Urval: ${s}`,
    scopeAll: 'hela organisationen',
    period12: 'Senaste tolv månaderna',
    periodYear: 'Innevarande år',
    periodMonth: 'Innevarande månad',
  },
  imports: {
    title: 'Inläsning',
    intro:
      'Läs in filer från Ayvens, körjournalen (Abax till januari 2027, sedan Boomr), det manuella serviceregistret och Fleet360. Du ser först vad varje rad gör, sedan genomför du.',
    kind: 'Typ av fil',
    kinds: {
      ayvens: 'Fordonskostnader från Ayvens',
      abax: 'Mätarställningar från Abax',
      boomr: 'Mätarställningar från Boomr',
      service: 'Servicehistorik från det manuella registret',
      fleet360: 'Fordon från Fleet360',
    } as Record<string, string>,
    file: 'Fil (CSV)',
    fileHelp: 'Semikolon, komma eller tabb som avgränsare. Första raden är kolumnrubriker.',
    upload: 'Granska filen',
    uploading: 'Läser filen',
    emptyFile: 'Filen är tom.',
    missingColumn: 'Kolumnen saknas i filen.',
    missingColumns: (cols: string) => `Filen saknar kolumnerna: ${cols}.`,
    columnsFound: 'Kolumner i filen',
    columnsHelp: 'Kolumnerna hittas på rubriken. Dessa rubriker förstås',
    fields: {
      plate: 'Registreringsnummer',
      date: 'Datum',
      category: 'Kategori',
      amount: 'Belopp',
      description: 'Beskrivning',
      reference: 'Fakturanummer',
      readAt: 'Tidpunkt',
      km: 'Mätarställning',
      doneOn: 'Datum',
      kind: 'Typ',
      odometerKm: 'Mätarställning',
      workshop: 'Verkstad',
      costSek: 'Kostnad',
      nextDueOn: 'Nästa service',
      externalId: 'Id i Fleet360',
      vehicleType: 'Fordonstyp',
      make: 'Märke',
      model: 'Modell',
      modelYear: 'Årsmodell',
      forvaltning: 'Förvaltning',
      unit: 'Avdelning',
      managerName: 'Chef',
      coordinatorName: 'Fordonssamordnare',
      representativeName: 'Fordonsombud',
      parking: 'Parkering',
      charger: 'Laddstolpe',
      dimAnsvar: 'Ansvar',
      dimTjanst: 'Tjänst',
      dimAktivitet: 'Aktivitet',
      dimObjekt: 'Objekt',
      dimMotpart: 'Motpart',
      codeStrings: 'Kodsträngar',
      ownership: 'Ägandeform',
      lessor: 'Finansbolag',
      leaseEnd: 'Leasing till',
      leaseMonthlySek: 'Leasingavgift',
      notes: 'Anteckningar',
      equipment: 'Utrustning',
    } as Record<string, string>,
    preview: 'Granskning',
    stats: (p: { rows: number; ny: number; andrad: number; oforandrad: number; fel: number }) =>
      `${p.rows} rader: ${p.ny} nya, ${p.andrad} ändras, ${p.oforandrad} finns redan, ${p.fel} med fel`,
    actions: { ny: 'Ny', andrad: 'Ändras', oforandrad: 'Finns redan', fel: 'Fel' } as Record<
      string,
      string
    >,
    errors: { unknown_vehicle: 'Fordonet finns inte i registret' } as Record<string, string>,
    errorField: (f: string) => `${f} saknas eller går inte att läsa`,
    apply: 'Genomför inläsningen',
    applied: (r: { created: number; updated: number; skipped: number }) =>
      `Klart. ${r.created} nya, ${r.updated} ändrade, ${r.skipped} hoppades över.`,
    history: 'Tidigare inläsningar',
    status: {
      granskad: 'Granskad',
      genomford: 'Genomförd',
      misslyckad: 'Misslyckad',
      avbruten: 'Avbruten',
    } as Record<string, string>,
    sample: 'De första raderna',
    row: 'Rad',
  },
  billing: {
    title: 'Debitering',
    intro:
      'Skapa debiteringsfilen för en period: bilpoolens bokningar på bokarens kostnadsställe, fordonskostnader och verkstadskostnader på fordonets ansvar. En summarad per kund är samlingsfakturan. Filen läses in i ekonomisystemet.',
    period: 'Period',
    preview: 'Visa underlaget',
    create: 'Skapa debiteringsfilen',
    created: 'Debiteringsfilen är skapad.',
    customer: 'Kund (kostnadsställe eller ansvar)',
    bilpool: 'Bilpool',
    fordon: 'Fordonskostnader',
    verkstad: 'Verkstad',
    total: 'Summa',
    lines: 'Rader',
    download: 'Hämta filen',
    runs: 'Skapade filer',
    empty: 'Inget att debitera i perioden.',
    perCustomer: 'Samlingsfaktura per kund',
    sampleLines: 'De första raderna i filen',
    status: { skapad: 'Skapad', skickad: 'Skickad', misslyckad: 'Misslyckad' } as Record<
      string,
      string
    >,
    sftpNote:
      'Filen levereras till ekonomisystemet med SFTP när adressen är inställd. Tills dess hämtas den här.',
  },
  adminHome: {
    title: 'Administration',
    intro: 'Inställningar och verktyg för organisationen. Ändringar loggas med namn och tidpunkt.',
    sections: {
      imports: 'Inläsning av filer',
      billing: 'Debiteringsfil',
      keys: 'Nyckelskåp',
      pools: 'Bilpooler',
      fields: 'Fritextfält',
      roles: 'Roller och behörigheter',
      idp: 'Inloggning',
      audit: 'Logg',
      api: 'API och integrationer',
    } as Record<string, string>,
    descriptions: {
      imports: 'Ayvens, körjournal, serviceregister, Fleet360.',
      billing: 'Debiteringsfil och samlingsfaktura per period.',
      keys: 'Skåp, platser och koppling av nycklar.',
      pools: 'Pooler, priser, prioriteter och medlemmar.',
      fields: 'Fritextfälten på fordonskortet.',
      roles: 'Vem som får göra vad.',
      idp: 'OpenID Connect eller SAML 2.0.',
      audit: 'Vem som läst och ändrat vad.',
      api: 'Nycklar för andra system och API-dokumentationen.',
    } as Record<string, string>,
  },
  api: {
    title: 'API och integrationer',
    intro:
      'Andra system hämtar bokningar och fordon och lämnar mätarställningar genom tjänstens API. Varje system får en egen klient med de behörigheter det behöver. Hemligheten visas en gång.',
    docs: 'Dokumentation av API:et',
    name: 'Systemets namn',
    scopes: 'Behörigheter',
    scopeNames: {
      'bokningar.las': 'Hämta bokningar',
      'fordon.las': 'Hämta fordonsregistret',
      'matarstallning.skriv': 'Lämna mätarställningar från körjournalen',
    } as Record<string, string>,
    create: 'Skapa klient',
    created: 'Klienten är skapad. Kopiera hemligheten nu, den visas inte igen.',
    clientId: 'Klient-id',
    secret: 'Hemlighet',
    clients: 'Klienter',
    clientsEmpty: 'Inga klienter.',
    lastUsed: 'Senast använd',
    active: 'Aktiv',
    inactive: 'Avstängd',
    deactivate: (name: string) => `Stäng av ${name}`,
    deactivateShort: 'Stäng av',
    rateLimited: 'För många anrop. Vänta en minut och försök igen.',
    invalidToken: 'Åtkomsttoken saknas, är ogiltig eller har gått ut.',
    scope: (scope: string) => `Klienten saknar behörigheten ${scope}.`,
    noScopes: 'Välj minst en behörighet.',
    docsTitle: 'Aldorock Fordon API',
    required: 'obligatorisk',
    docsOauth:
      'grant_type=client_credentials. Klient-id och hemlighet skickas med HTTP Basic. Svaret innehåller access_token, en JWT enligt RFC 9068 som gäller en timme.',
    docsIntro:
      'REST över HTTPS med JSON. Fel returneras som Problem Details enligt RFC 9457. Autentisering med OAuth 2.0 client credentials mot /oauth/token. Den maskinläsbara beskrivningen (OpenAPI 3.1) finns på /api/v1/openapi.json och kan användas av vilket verktyg som helst utan leverantörsspecifik teknik.',
    docsSecurityTitle: 'Säkerhet',
    docsSecurity:
      'All trafik går över TLS 1.3. Varje anrop bär en Bearer-token som är bunden till en klient och en organisation, kontrolleras mot klientens status vid varje anrop och begränsas till 600 anrop per minut. Alla anrop loggas med klient, tidpunkt och vad som hämtades.',
  },
  idp: {
    title: 'Inloggning',
    intro:
      'Användarna loggar in med organisationens egna konton genom OpenID Connect eller SAML 2.0. Här anger ni anslutningen till er identitetsleverantör, till exempel Nexus Digital Access, Microsoft Entra ID, Keycloak eller ADFS, för testmiljön och för produktionsmiljön. Inga lösenord sparas i tjänsten.',
    protocol: 'Protokoll',
    protocols: { oidc: 'OpenID Connect', saml: 'SAML 2.0' } as Record<string, string>,
    acs: 'Adress dit identitetsleverantören skickar svaret (SAML)',
    spMetadata: 'Tjänstens SAML-metadata',
    spCert: 'Tjänstens certifikat för signering av SAML-request',
    spCertHelp:
      'Tjänsten signerar varje SAML-request med den här nyckeln och godtar bara signerade svar. Certifikatet finns också i metadatan.',
    samlEntityId: 'Identitetsleverantörens entity ID',
    samlEntityIdHelp:
      'Står i identitetsleverantörens metadata, till exempel https://idp.kommun.se/adfs/services/trust.',
    spEntityId: 'Tjänstens entity ID',
    spEntityIdHelp:
      'Det namn identitetsleverantören känner tjänsten under. Oftast adressen till tjänstens SAML-metadata.',
    samlSsoUrl: 'Adress för inloggning (SSO)',
    samlSsoUrlHelp: 'Identitetsleverantörens adress för SingleSignOnService med Redirect.',
    samlIdpCert: 'Identitetsleverantörens certifikat för signering',
    samlIdpCertHelp: (validTo: string | null) =>
      validTo
        ? `Ett certifikat är sparat och gäller till ${validTo}. Klistra in ett nytt bara om det ska bytas.`
        : 'Klistra in certifikatet från identitetsleverantörens metadata, med eller utan raderna BEGIN och END.',
    ssoHttps: 'Adressen för inloggning måste nås med https.',
    certInvalid: 'Certifikatet går inte att läsa. Kontrollera att hela texten kom med.',
    certMissing: 'Inget certifikat är sparat för SAML.',
    certExpired: (date: string) => `Certifikatet gick ut ${date}. Klistra in det nya.`,
    samlOk: (date: string) =>
      `Inställningarna för SAML är kompletta. Certifikatet gäller till ${date}. Prova sedan att logga in.`,
    active: (env: string) => `Den här installationen använder ${env}.`,
    environments: { test: 'testmiljön', production: 'produktionsmiljön' } as Record<string, string>,
    sectionTitle: { test: 'Testmiljö', production: 'Produktionsmiljö' } as Record<string, string>,
    redirect: 'Adress att registrera hos identitetsleverantören för inloggning',
    logout: 'Adress att registrera för utloggning',
    displayName: 'Namn',
    issuer: 'Utfärdare (issuer)',
    issuerHelp:
      'Adressen till identitetsleverantören, till exempel https://login.microsoftonline.com/<tenant>/v2.0.',
    clientId: 'Klient-id',
    clientSecret: 'Klienthemlighet',
    clientSecretHelp: (has: boolean) =>
      has
        ? 'En hemlighet är sparad. Fyll i en ny bara om den ska bytas.'
        : 'Fyll i hemligheten från identitetsleverantören.',
    scopes: 'Scopes',
    groupsClaim: 'Attribut med grupper',
    groupsClaimHelp:
      'Grupperna styr rollerna. Kopplingen mellan grupp och roll görs under Roller och behörigheter.',
    personClaim: 'Attribut som identifierar personen',
    personClaimHelp:
      'Beständig identifierare, till exempel oid eller employeeId. E-post går att byta maskinellt under Roller och behörigheter.',
    enabled: 'Anslutningen är aktiv',
    save: (env: string) => `Spara ${env}`,
    saved: 'Anslutningen är sparad.',
    test: (env: string) => `Testa anslutningen till ${env}`,
    testOk: (issuer: string) =>
      `Anslutningen fungerar. Identitetsleverantören svarar som ${issuer}.`,
    testFailed: (why: string) => `Anslutningen fungerar inte: ${why}`,
    notConfigured: 'Ingen anslutning är sparad för den här miljön.',
    issuerHttps: 'Utfärdaren måste nås med https.',
    openid: 'Scopes måste innehålla openid.',
  },
  rolesAdmin: {
    title: 'Roller och behörigheter',
    intro:
      'Behörighet styrs av roll. Alla som loggar in blir bokare. En roll ges genom en grupp hos identitetsleverantören eller direkt till en användare, för hela organisationen eller för en förvaltning med dess avdelningar. Ändras en roll gäller det alla som har den.',
    matrix: 'Vad varje roll får göra',
    function: 'Funktion',
    groups: 'Grupper hos identitetsleverantören',
    group: 'Grupp',
    role: 'Roll',
    unit: 'Gäller',
    wholeOrg: 'Hela organisationen',
    addGroup: 'Koppla grupp till roll',
    addGroupButton: 'Koppla',
    removeGroup: (g: string) => `Ta bort kopplingen för ${g}`,
    users: 'Användare och roller',
    user: 'Användare',
    lastLogin: 'Senast inloggad',
    sources: { manual: 'given direkt', idp: 'från grupp', auto: 'automatiskt' } as Record<
      string,
      string
    >,
    addRole: 'Ge en användare en roll',
    validTo: 'Gäller till och med',
    validToHelp: 'Lämna tomt om rollen gäller tills vidare.',
    addRoleButton: 'Ge rollen',
    removeRole: (role: string, user: string) => `Ta bort ${role} från ${user}`,
    removeShort: 'Ta bort',
    added: 'Sparat. Gäller från nästa sidvisning.',
    searchUser: 'Sök användare',
    rekey: 'Byt identifierare',
    rekeyHelp:
      'Om identitetsleverantören byter en användares identifierare (till exempel e-post) kopplas kontot om här. Historiken följer med.',
    rekeyLabel: 'Ny identifierare (subject)',
    rekeyButton: 'Byt',
    rekeyed: 'Identifieraren är bytt.',
    disable: (user: string) => `Stäng av ${user}`,
    enable: (user: string) => `Aktivera ${user}`,
    active: 'Aktiv',
    disabled: 'Avstängd',
    permissions: {
      'start.view': 'Se startsidan',
      'vehicle.read': 'Se fordonsregistret och fordonskort',
      'vehicle.write': 'Registrera och ändra fordon',
      'vehicle.register.refresh': 'Hämta från vägtrafikregistret',
      'cost.read': 'Se kostnader',
      'cost.write': 'Registrera kostnader',
      'workshop.write': 'Registrera verkstadsjobb',
      'pool.manage': 'Administrera bilpooler',
      'key.manage': 'Administrera nyckelskåp',
      'booking.own': 'Boka bil',
      'booking.others': 'Boka åt andra',
      'booking.manage': 'Ändra alla bokningar, ej debiterbara',
      'calendar.view': 'Se kalendern',
      'report.view': 'Se rapporter',
      'report.manage': 'Dela rapportmallar',
      'import.run': 'Läsa in filer',
      'export.run': 'Exportera all data',
      'billing.run': 'Skapa debiteringsfil',
      'admin.view': 'Se administrationen',
      'role.manage': 'Hantera roller',
      'settings.manage': 'Inloggning, API och inställningar',
      'audit.view': 'Läsa loggen',
      'koncern.manage': 'Skapa organisationer i koncernen',
    } as Record<string, string>,
  },
  audit: {
    title: 'Logg',
    intro:
      'Varje läsning av ett fordonskort, varje ändring, varje inloggning och varje nekat anrop loggas med användare och tidpunkt. Loggen kan inte ändras, och kedjan av poster kontrolleras här.',
    actor: 'Användare',
    action: 'Händelse',
    all: 'Alla',
    from: 'Från',
    to: 'Till',
    filter: 'Visa',
    export: 'Exportera CSV',
    verify: 'Kontrollera loggen',
    verifiedOk: (n: number) => `Loggen är hel. ${n} poster kontrollerade.`,
    verifiedBroken: (seq: number) => `Kedjan är bruten vid post ${seq}. Kontakta leverantören.`,
    rows: 'Poster',
    when: 'Tidpunkt',
    object: 'Objekt',
    fields: 'Fält',
    security: 'Säkerhetshändelser',
    securityEmpty: 'Inga säkerhetshändelser.',
    actions: {
      read: 'Läsning',
      create: 'Skapad',
      update: 'Ändrad',
      delete: 'Borttagen',
      login: 'Inloggning',
      logout: 'Utloggning',
      denied: 'Nekad begäran',
      search: 'Sökning',
      report: 'Rapport',
      export: 'Export',
      register_lookup: 'Registeruppslag',
      import_preview: 'Inläsning granskad',
      import_apply: 'Inläsning genomförd',
      billing_run: 'Debiteringsfil',
      api_bokningar: 'API: bokningar',
      api_fordon: 'API: fordon',
      api_matarstallningar: 'API: mätarställningar',
      full_export: 'Fullständig export',
    } as Record<string, string>,
    objects: {
      vehicle: 'Fordon',
      booking: 'Bokning',
      app_user: 'Användare',
      role_assignment: 'Roll',
      idp_group_mapping: 'Gruppkoppling',
      idp_config: 'Inloggning',
      pool: 'Bilpool',
      pool_vehicle: 'Fordon i pool',
      pool_member: 'Poolmedlem',
      key_cabinet: 'Nyckelskåp',
      key_slot: 'Nyckelplats',
      vehicle_cost: 'Kostnad',
      service_record: 'Service',
      workshop_job: 'Verkstadsjobb',
      custom_field: 'Fritextfält',
      vehicle_custom_value: 'Fritext',
      report_definition: 'Rapportmall',
      report: 'Rapport',
      import_batch: 'Inläsning',
      billing_run: 'Debiteringsfil',
      api_client: 'API-klient',
      api: 'API',
      request: 'Begäran',
      session: 'Session',
      tenant: 'Organisation',
      org_unit: 'Enhet',
      export: 'Export',
    } as Record<string, string>,
    securityKinds: {
      login_denied: 'Inloggning nekad, kontot saknar behörighet',
      login_refused_by_idp: 'Identitetsleverantören nekade inloggningen',
      saml_rejected: 'SAML-svar avvisat',
    } as Record<string, string>,
  },
  fieldsAdmin: {
    title: 'Fritextfält',
    intro:
      'Fritextfälten på fordonskortet bestäms tillsammans vid uppstart (svar 1) och ändras här. Ett nytt fält syns på alla fordonskort, i rapportbyggaren och i exporten direkt.',
    key: 'Nyckel',
    label: 'Rubrik',
    add: 'Lägg till fält',
    addButton: 'Lägg till',
    rename: 'Byt rubrik',
    remove: (label: string) => `Ta bort ${label}`,
    removeShort: 'Ta bort',
    removeHelp: 'Fältet och dess texter på alla fordon tas bort. Det går inte att ångra.',
    saved: 'Sparat.',
    up: (label: string) => `Flytta ${label} uppåt`,
    down: (label: string) => `Flytta ${label} nedåt`,
    keyHelp: 'Små bokstäver utan mellanslag, till exempel skador. Används i export och API.',
    keyTaken: 'Nyckeln används redan.',
  },
  exportAdmin: {
    title: 'Export av all data',
    intro:
      'Hela organisationens data i öppet format, utan kostnad och utan volymgräns: varje tabell som JSON, uppladdade filer som de är, och en förteckning med antal och kontrollsummor. Används vid avveckling eller för egna analyser.',
    button: 'Skapa exporten',
    creating: 'Skapar exporten',
    done: 'Exporten är klar och hämtas.',
    manifest: 'Förteckning',
  },
  entry: {
    addCost: 'Registrera kostnad',
    addService: 'Registrera service eller besiktning',
    addOdometer: 'Registrera mätarställning',
    addWorkshop: 'Registrera verkstadsjobb',
    costDate: 'Datum',
    category: 'Kategori',
    amount: 'Belopp (kr)',
    supplier: 'Leverantör',
    description: 'Beskrivning',
    reference: 'Referens',
    doneOn: 'Utfört',
    kind: 'Typ',
    odometerKm: 'Mätarställning (km)',
    workshop: 'Verkstad',
    costSek: 'Kostnad (kr)',
    nextDueOn: 'Nästa gång senast',
    nextDueKm: 'Nästa gång vid (km)',
    readAt: 'Avläst',
    km: 'Mätarställning (km)',
    jobDescription: 'Vad gjordes',
    hours: 'Timmar',
    hourlyRate: 'Timpris (kr)',
    parts: 'Delar (kr)',
    mechanic: 'Mekaniker',
    openedOn: 'Öppnat',
    closeJob: 'Avsluta jobbet och bokför kostnaden',
    jobTotal: (n: string) => `Summa ${n}`,
    save: 'Spara',
    saved: 'Sparat.',
    cancel: 'Avbryt',
    workshopJobs: 'Verkstadsjobb',
    workshopEmpty: 'Inga verkstadsjobb.',
    jobStatus: { oppen: 'Pågår', klar: 'Klart', fakturerad: 'Debiterat' } as Record<string, string>,
    number: 'Nummer',
  },
  ical: {
    attachment: 'Kalenderfil',
    download: 'Lägg i kalendern (ics)',
    summary: (vehicle: string) => `Bokad bil: ${vehicle}`,
    description: (p: { garage: string; code: string | null; number: string }) =>
      `Garage: ${p.garage}${p.code ? `\nKod till nyckelskåpet: ${p.code}` : ''}\nBokning ${p.number}`,
    reminderSubject: (vehicle: string, time: string) => `Påminnelse: ${vehicle} i morgon ${time}`,
  },
  board: {
    title: 'Tavla',
    intro:
      'Läget i bilpoolerna just nu, för en skärm i garaget eller receptionen. Uppdateras varje minut.',
    nav: 'Tavla',
    pool: (name: string) => name,
    free: 'Lediga nu',
    out: 'Ute nu',
    next: 'Nästa bokning',
    today: 'Bokningar i dag',
    none: 'Inga',
    until: (time: string) => `till ${time}`,
    from: (time: string) => `från ${time}`,
    updated: (time: string) => `Uppdaterad ${time}`,
    fleet: 'Fordonsparken',
    vehicles: 'Fordon',
    inspections: 'Besiktning inom 30 dagar',
    keyOut: 'Nycklar ute',
  },
  documents: {
    title: 'Dokument',
    empty: 'Inga dokument.',
    upload: 'Ladda upp dokument',
    file: 'Fil',
    fileHelp: 'PDF, Word, Excel, JPEG eller PNG. Högst 25 MB.',
    description: 'Beskrivning',
    uploadButton: 'Ladda upp',
    uploaded: 'Dokumentet är sparat.',
    download: (name: string) => `Ladda ner ${name}`,
    remove: (name: string) => `Ta bort ${name}`,
    removeShort: 'Ta bort',
    tooLarge: 'Filen är för stor. Största tillåtna storlek är 25 MB.',
    wrongType: 'Filtypen går inte att ladda upp. Tillåtna typer är PDF, Word, Excel, JPEG och PNG.',
    missingFile: 'Välj en fil att ladda upp.',
    fleetZip: 'Filer från Fleet360 (zip)',
    fleetZipHelp:
      'En zip med en mapp per registreringsnummer. Varje fil i mappen läggs på det fordonet.',
    fleetZipButton: 'Läs in filerna',
    fleetZipDone: (r: { attached: number; skipped: number; unknown: string[] }) =>
      `${r.attached} filer bifogade, ${r.skipped} hoppades över${r.unknown?.length ? `, okända registreringsnummer: ${r.unknown.join(', ')}` : ''}.`,
  },
  schedules: {
    title: 'Schemalagda utskick',
    intro:
      'Rapporten skickas med e-post till valda mottagare varje vecka eller månad, för veckan eller månaden före.',
    name: 'Namn på utskicket',
    recipients: 'Mottagare (e-post, en per rad)',
    cadence: 'Hur ofta',
    cadences: { veckovis: 'Varje vecka', manadsvis: 'Varje månad' } as Record<string, string>,
    weekday: 'Veckodag',
    weekdays: ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'],
    dayOfMonth: 'Dag i månaden',
    format: 'Format',
    formats: { xlsx: 'Excel', csv: 'CSV' } as Record<string, string>,
    create: 'Schemalägg utskicket',
    created: 'Utskicket är schemalagt.',
    sendNow: (name: string) => `Skicka ${name} nu`,
    sendNowShort: 'Skicka nu',
    sent: (n: number) => (n === 1 ? 'Skickat till 1 mottagare.' : `Skickat till ${n} mottagare.`),
    remove: (name: string) => `Ta bort utskicket ${name}`,
    removeShort: 'Ta bort',
    lastSent: 'Senast skickat',
    never: 'Aldrig',
    empty: 'Inga schemalagda utskick.',
    subject: (name: string, from: string, to: string) => `${name}: rapport ${from} till ${to}`,
    body: (name: string, from: string, to: string, rows: number) =>
      `Hej,\n\nHär är rapporten ${name} för perioden ${from} till ${to}, ${rows} rader. Rapporten är bifogad.\n\nAldorock Fordon`,
    attachment: 'Bilaga',
  },
  admin: {
    title: 'Administration',
    intro: 'Inställningar för organisationen. Ändringar loggas med namn och tidpunkt.',
    sections: {
      roles: 'Roller och behörigheter',
      idp: 'Inloggning',
      audit: 'Logg',
      fields: 'Fritextfält',
      org: 'Organisation',
    },
  },
  errors: {
    notFound: 'Sidan finns inte.',
    forbidden: 'Du har inte behörighet till det här.',
    unauthenticated: 'Du behöver logga in.',
    server: 'Något gick fel hos oss. Försök igen om en stund. Felet är loggat.',
    network: 'Det gick inte att nå Aldorock Fordon. Kontrollera anslutningen och försök igen.',
    validation: 'Uppgifterna behöver rättas innan de kan sparas.',
    backToStart: 'Till startsidan',
  },
  table: {
    scrollLabel: (name: string) => `${name}, rullbar tabell`,
    phoneNote: 'Fler kolumner visas på en bredare skärm.',
    empty: 'Inget att visa.',
  },
  forms: {
    required: (label: string) => `Fyll i ${label.toLowerCase()}.`,
    invalid: (label: string) =>
      `Kontrollera ${label.toLowerCase()}. Värdet går inte att spara som det står.`,
    summary: 'Uppgifterna behöver rättas innan de kan sparas:',
    saving: 'Sparar',
    choose: 'Välj',
    saved: 'Sparat',
    requiredMark: 'obligatoriskt',
  },
  help: {
    button: 'Hjälp',
    close: 'Stäng hjälpen',
    title: (screen: string) => `Hjälp: ${screen}`,
    screens: {
      start: 'Startsidan',
      book: 'Boka bil',
      booking: 'Bokning',
      calendar: 'Kalender',
      vehicles: 'Fordon',
      vehicle: 'Fordonskort',
      newVehicle: 'Nytt fordon',
      pools: 'Bilpooler',
      reports: 'Rapporter',
      messages: 'Meddelanden',
      admin: 'Administration',
      keys: 'Nyckelskåp',
      poolAdmin: 'Administrera bilpooler',
      imports: 'Inläsning',
      billing: 'Debitering',
      idp: 'Inloggning',
      roles: 'Roller och behörigheter',
      audit: 'Logg',
      fields: 'Fritextfält',
      export: 'Export',
    } as Record<string, string>,
    texts: {
      start:
        'Startsidan visar dina kommande bokningar med knappar för att ändra eller avboka. Boka igen upprepar din senaste resa med samma bil och tider, så att du bara behöver bekräfta. Administratörer ser dessutom läget i fordonsparken.',
      book: 'Välj datum och tid, sedan bil. Bilarna visas i administratörens prioritetsordning. Kostnadsstället är förifyllt från din profil. När du bekräftar får du garage och kod till nyckelskåpet på skärmen och med e-post.',
      booking:
        'Här ändrar du tid, bil eller förare, eller avbokar. Varje ändring ger en ändringsbekräftelse med e-post. Nyckeln kan hämtas från en kvart före starttiden till en kvart efter sluttiden.',
      calendar:
        'Kalendern visar en vecka i taget per bilpool, en rad per bil. Du ser vem som har bokat. Klicka på Boka i en ledig dag för att börja en bokning med dagen förvald.',
      vehicles:
        'Fordonsregistret visar alla fordon. Sök på registreringsnummer, märke, modell eller förvaltning och filtrera på typ och status. Öppna ett fordon för att se fordonskortet.',
      vehicle:
        'Fordonskortet samlar allt om fordonet: uppgifterna från vägtrafikregistret, era egna uppgifter om förvaltning, kontakter och kontering, fritextfält, mätarställning, kostnader, servicehistorik, bokningar och historik. Hämta från vägtrafikregistret uppdaterar registeruppgifterna på begäran.',
      newVehicle:
        'Skriv registreringsnumret och hämta. Registret fyller i tekniska uppgifter, besiktning och skatt. Du fyller i det som bara ni vet och sparar. Fordonskortet är klart direkt.',
      pools:
        'Bilpoolerna med garage, nyckelskåp, pris och vilka fordon som ingår. Administratören sätter prioritetsordning, information per fordon och vilka medarbetare som har tillgång.',
      reports:
        'Välj kolumner, filter och period. Rapporten visas som tabell och kan exporteras till Excel eller CSV.',
      messages:
        'Samma meddelanden som har skickats till din e-post, så att du alltid hittar koden.',
      admin: 'Inställningar för organisationen: roller, inloggning, fritextfält och loggen.',
      keys: 'Varje skåp har numrerade platser. Koppla ett registreringsnummer till en plats så följer koden i bokningsbekräftelsen med till rätt nyckel. Prova skåpet visar vad skåpet svarar på en kod just nu.',
      idp: 'Anslutningen till identitetsleverantören för testmiljön och produktionsmiljön. Testa anslutningen innan du aktiverar den.',
      roles:
        'Koppla grupper hos identitetsleverantören till roller, eller ge en användare en roll direkt. Byt identifierare om identitetsleverantören byter en användares id.',
      audit:
        'Filtrera på användare, händelse och tid. Exportera som CSV. Kontrollera loggen räknar om kedjan av poster.',
      fields:
        'Lägg till, byt rubrik på eller ta bort fritextfält. Ordningen är den på fordonskortet.',
      export: 'Skapa exporten och hämta zip-filen. Varje tabell är en JSON-fil.',
      imports:
        'Välj typ av fil och läs in den. Granskningen visar varje rad med vad den gör: ny, ändras, finns redan eller fel. Genomför när det ser rätt ut. Fel rader hoppas över, resten läses in.',
      billing:
        'Välj period och se underlaget per kund. Skapa filen när underlaget stämmer. Varje fil sparas och kan hämtas igen.',
      poolAdmin:
        'Ändra namn, garage, pris och gränser per pool. Lägg till fordon, sätt prioritet och information per bil, byt ut en bil med bokningar, och välj vilka medarbetare som når en pool som inte är standardpool.',
    } as Record<string, string>,
  },
  mockIdp: {
    title: 'Testinloggning',
    intro:
      'Det här är den lokala testinloggningen med påhittade användare. Välj vem du vill logga in som.',
    loggedOut: 'Du är utloggad från testinloggningen.',
  },
} as const;

export type Sv = typeof sv;
